// RegistryDetails/index.tsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
	useGetNodeQuery,
	useDownloadRegistryDocumentsMutation,
	DownloadRegistryDocumentsInput,
	DownloadRegistryDocumentsMutation,
	RegistryDocuments,
	Registry,
	OnboardingStatus,
	RegistryStatus,
	AntifraudAnalysisType
} from '@/graphql/__generated__/onboarding-legal';

const UNICO_ANTIFRAUD_TYPE = AntifraudAnalysisType.V2;
import { useTranslations } from 'use-intl';
import { toTranslationKey } from '@/utils/translations';
import { Typography } from '@/UIlibrary/Typography/Typography';
import {
	AlertTriangle,
	ArrowLeft,
	ChevronsDown,
	ChevronsUp,
	CheckCircle,
	AlertCircle,
	XCircle,
	Clock
} from 'react-feather';
import Button from '@/UIlibrary/Button/Button';
import { BRAND_COLORS, GRAY_COLORS } from '@/UIlibrary/globalTheme';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import Modal from '@/UIlibrary/Modal/Modal';
import { Tooltip } from '@/UIlibrary/Tooltip/Tooltip';
import IconButton from '@/UIlibrary/IconButton/IconButton';
import StatusTimeline from './RegistryDetails/StatusTimeline';
import RegistrySection from './RegistryDetails/RegistrySection';
import ContactInformation from './RegistryDetails/ContactInformation';
import DocumentsSection from './RegistryDetails/DocumentsSection';
import { JsonView, allExpanded, defaultStyles } from 'react-json-view-lite';
import logger from '@/logger';
import GeolocationAddress from './RegistryDetails/AntifraudAnalysis/GeolocationAddress';
import OfficialId from './RegistryDetails/AntifraudAnalysis/OfficialId';
import IpAddress from './RegistryDetails/AntifraudAnalysis/IpAddress';
import BlackListsPEP from './RegistryDetails/AntifraudAnalysis/BlackListsPEP';
import {
	useNormalizedAntifraudData,
	useSeparatedAntifraudData
} from '@/hooks/useNormalizedAntifraudData';
import { NormalizedAntifraudData } from '@/utils/antifraudDataNormalizer';
import * as Sentry from '@sentry/react';
import { OnboardingStatusBadge } from '@/pages/Onboarding/OnboardingStatusBadge';
import CompanyInformation from './RegistryDetails/CompanyInformation';
import Curp from './RegistryDetails/AntifraudAnalysis/Curp';
import Rfc from './RegistryDetails/AntifraudAnalysis/Rfc';
import LegalRepresentative from './RegistryDetails/LegalRepresentative';
import DeliberationDrawer from './RegistryDetails/DeliberationDrawer';
import ApproveRegistryModal from './RegistryDetails/ApproveRegistryModal';
import RejectRegistryModal from './RegistryDetails/RejectRegistryModal';
import toast from 'react-hot-toast';

export interface ChangesRequestEntry {
	section: string;
	field: string;
	value: any;
}

const LoadingSpinner = () => (
	<div className="flex justify-center items-center h-screen">
		<div className="loader"></div>
	</div>
);

const ErrorMessage: React.FC<{ message: string }> = ({ message }) => (
	<div className="flex items-center gap-2 p-4 text-red-600 bg-red-50 rounded-md">
		<Typography size="sm">{message}</Typography>
	</div>
);

const Header: React.FC<{ registry: any; openDeliverationDrawer: () => void }> = ({
	registry,
	openDeliverationDrawer
}) => {
	const t = useTranslations('RegistryDetails');
	const fullName = registry.companyInformation?.legalName;

	return (
		<div className="flex flex-row justify-between border-b border-slate-200 py-4 w-4/5 2xl:w-full 2xl:max-w-[1450px] z-40 fixed top-0 bg-white">
			<div className="flex items-center gap-4">
				<div className="flex flex-col gap-4">
					<div className="flex flex-row gap-4 items-center">
						<Link to="/clients">
							<ArrowLeft size={24} color={BRAND_COLORS.brand600} />
						</Link>
						<Typography size="md" type="display">
							{fullName}
						</Typography>
						&#x2022;
						<OnboardingStatusBadge
							status={registry.onboardingStatus as OnboardingStatus}
							size="md"
						/>
					</div>
					<div className="ml-10">
						<Typography size="md" color="subtitle">
							{t('ClientID')}: {registry.clientId}
						</Typography>
					</div>
				</div>
			</div>
			{registry?.deliberation && (
				<div className="flex items-center">
					<Button label={t('DeliberationLabel')} size="auto" onClick={openDeliverationDrawer} />
				</div>
			)}
		</div>
	);
};

const SectionControls: React.FC<{
	title: string;
	openSections: Set<string>;
	sectionTitles: string[];
	onExpandAll: () => void;
	onCollapseAll: () => void;
}> = ({ title, openSections, sectionTitles, onExpandAll, onCollapseAll }) => {
	const t = useTranslations('RegistryDetails');

	return (
		<div className="flex flex-row gap-2 items-center justify-end">
			<div className="grow">
				<Typography weight="bold" size="xl">
					{title}
				</Typography>
			</div>
			<Tooltip content={t('ExpandAll')}>
				<IconButton
					icon={<ChevronsDown />}
					onClick={onExpandAll}
					size="xsmall"
					disabled={openSections.size === sectionTitles.length}
					color="gray"
					dataTestId="expand-all-button"
				/>
			</Tooltip>
			<Tooltip content={t('CollapseAll')}>
				<IconButton
					icon={<ChevronsUp />}
					onClick={onCollapseAll}
					size="xsmall"
					disabled={openSections.size === 0}
					color="gray"
					dataTestId="collapse-all-button"
				/>
			</Tooltip>
		</div>
	);
};

const normalizeUnicoResultForDisplay = (result: string | null | undefined): string | null => {
	if (!result) return null;
	const upperResult = result.toUpperCase();
	const enumToDisplayMap: Record<string, string> = {
		PROCESS_RESULT_ERROR: 'ERROR',
		PROCESS_RESULT_EN_VIVO: 'EN VIVO',
		PROCESS_RESULT_LIVE: 'EN VIVO',
		PROCESS_RESULT_NOT_EN_VIVO: 'NO EN VIVO',
		PROCESS_RESULT_HIGH_RISK: 'ALTO RIESGO',
		PROCESS_RESULT_CRITICAL_RISK: 'RIESGO CRITICO',
		PROCESS_RESULT_NOT_APPROVED: 'ALTO RIESGO'
	};
	const normalized = enumToDisplayMap[upperResult];
	if (normalized) return normalized;
	const displayValues = [
		'ERROR',
		'EN VIVO',
		'NO EN VIVO',
		'ALTO RIESGO',
		'RIESGO CRITICO',
		'NO APROBADO'
	];
	if (displayValues.includes(upperResult)) return upperResult;
	return 'ERROR';
};

const getUnicoResultColor = (result: string | null | undefined) => {
	if (!result) return { backgroundColor: 'rgba(128, 128, 128, 0.3)', color: 'gray' };
	const normalizedResult = normalizeUnicoResultForDisplay(result);
	if (!normalizedResult) return { backgroundColor: 'rgba(128, 128, 128, 0.3)', color: 'gray' };
	const upperResult = normalizedResult.toUpperCase();
	if (upperResult === 'ERROR') {
		return { backgroundColor: 'rgba(128, 128, 128, 0.3)', color: 'gray' };
	}
	if (upperResult === 'EN VIVO') {
		return { backgroundColor: 'rgba(16, 130, 82, 0.3)', color: '#108252' };
	}
	if (upperResult === 'NO EN VIVO') {
		return { backgroundColor: 'rgba(255, 167, 26, 0.3)', color: '#FFA71A' };
	}
	if (upperResult === 'ALTO RIESGO') {
		return { backgroundColor: 'rgba(255, 37, 26, 0.3)', color: '#FF251A' };
	}
	if (upperResult === 'RIESGO CRITICO' || upperResult === 'NO APROBADO') {
		return { backgroundColor: 'rgba(139, 0, 0, 0.3)', color: '#8B0000' };
	}
	return { backgroundColor: 'rgba(128, 128, 128, 0.3)', color: 'gray' };
};

const getUnicoResultIcon = (result: string | null | undefined) => {
	if (!result) return Clock;
	const normalizedResult = normalizeUnicoResultForDisplay(result);
	if (!normalizedResult) return Clock;
	const upperResult = normalizedResult.toUpperCase();
	if (upperResult === 'ERROR') {
		return AlertCircle;
	}
	if (upperResult === 'EN VIVO') {
		return CheckCircle;
	}
	if (upperResult === 'NO EN VIVO') {
		return AlertCircle;
	}
	if (upperResult === 'ALTO RIESGO') {
		return XCircle;
	}
	if (upperResult === 'RIESGO CRITICO' || upperResult === 'NO APROBADO') {
		return XCircle;
	}
	return Clock;
};

const getUnicoResultExplanation = (result: string | null | undefined): string | null => {
	if (!result) return null;
	const normalizedResult = normalizeUnicoResultForDisplay(result);
	if (!normalizedResult) return null;
	const upperResult = normalizedResult.toUpperCase();
	const explanations: Record<string, string> = {
		ERROR: 'UnicoError',
		'EN VIVO': 'UnicoEnVivo',
		'ALTO RIESGO': 'UnicoAltoRiesgo',
		'RIESGO CRITICO': 'UnicoRiesgoCritico',
		'NO APROBADO': 'UnicoNoAprobado',
		'NO EN VIVO': 'UnicoNoEnVivo'
	};
	return explanations[upperResult] || null;
};

const formatUnicoStatus = (status: string): string => {
	const normalizedStatus = normalizeUnicoResultForDisplay(status);
	if (!normalizedStatus) return status;
	const upperStatus = normalizedStatus.toUpperCase();
	if (upperStatus === 'EN VIVO') return 'En vivo';
	if (upperStatus === 'ALTO RIESGO') return 'Alto riesgo';
	if (upperStatus === 'RIESGO CRITICO') return 'Riesgo critico';
	if (upperStatus === 'NO APROBADO') return 'No aprobado';
	if (upperStatus === 'NO EN VIVO') return 'No en vivo';
	if (upperStatus === 'ERROR') return 'Error';
	return normalizedStatus;
};

const formatDisplayValue = (displayValue: string): string => {
	const upperValue = displayValue.toUpperCase();
	if (upperValue === 'EN VIVO') return 'En vivo';
	if (upperValue === 'ALTO RIESGO') return 'Alto riesgo';
	if (upperValue === 'RIESGO CRITICO') return 'Riesgo critico';
	if (upperValue === 'NO APROBADO') return 'No aprobado';
	if (upperValue === 'NO EN VIVO') return 'No en vivo';
	if (upperValue === 'ERROR') return 'Error';
	return displayValue;
};

const getDisplayValueColor = (displayValue: string) => {
	const upperValue = displayValue.toUpperCase();
	if (upperValue === 'ERROR') {
		return { backgroundColor: 'rgba(128, 128, 128, 0.3)', color: 'gray' };
	}
	if (upperValue === 'EN VIVO') {
		return { backgroundColor: 'rgba(16, 130, 82, 0.3)', color: '#108252' };
	}
	if (upperValue === 'NO EN VIVO') {
		return { backgroundColor: 'rgba(255, 167, 26, 0.3)', color: '#FFA71A' };
	}
	if (upperValue === 'ALTO RIESGO') {
		return { backgroundColor: 'rgba(255, 37, 26, 0.3)', color: '#FF251A' };
	}
	if (upperValue === 'RIESGO CRITICO' || upperValue === 'NO APROBADO') {
		return { backgroundColor: 'rgba(139, 0, 0, 0.3)', color: '#8B0000' };
	}
	return { backgroundColor: 'rgba(128, 128, 128, 0.3)', color: 'gray' };
};

const getDisplayValueIcon = (displayValue: string) => {
	const upperValue = displayValue.toUpperCase();
	if (upperValue === 'ERROR') {
		return AlertCircle;
	}
	if (upperValue === 'EN VIVO') {
		return CheckCircle;
	}
	if (upperValue === 'NO EN VIVO') {
		return AlertCircle;
	}
	if (upperValue === 'ALTO RIESGO') {
		return XCircle;
	}
	if (upperValue === 'RIESGO CRITICO' || upperValue === 'NO APROBADO') {
		return XCircle;
	}
	return Clock;
};

const UnicoRiskValuesModal: React.FC<{
	isOpen: boolean;
	onClose: () => void;
}> = ({ isOpen, onClose }) => {
	const t = useTranslations('RegistryDetails');

	const riskValues = [
		{ value: 'ERROR', explanationKey: 'UnicoError' },
		{ value: 'EN VIVO', explanationKey: 'UnicoEnVivo', displayKey: 'ProofOfLife' },
		{ value: 'ALTO RIESGO', explanationKey: 'UnicoAltoRiesgo' },
		{ value: 'RIESGO CRITICO', explanationKey: 'UnicoRiesgoCritico' },
		{ value: 'NO APROBADO', explanationKey: 'UnicoNoAprobado', displayKey: 'DoNotApprove' },
		{ value: 'NO EN VIVO', explanationKey: 'UnicoNoEnVivo', displayKey: 'ReviewProofOfLife' }
	];

	return (
		<Modal isOpen={isOpen} onClose={onClose} maxWidth={800} closeable={true}>
			<div className="flex flex-col gap-6">
				<Typography size="xl" weight="bold">
					{t('RiskValue')}
				</Typography>
				<Typography size="sm" color={GRAY_COLORS.gray600}>
					{t('RiskValuesDescription')}
				</Typography>
				<div className="grid grid-cols-2 gap-4">
					{riskValues.map((risk) => {
						const cardStyles = getDisplayValueColor(risk.value);
						const RiskIcon = getDisplayValueIcon(risk.value);
						const displayValue = risk.displayKey
							? t(toTranslationKey(risk.displayKey))
							: formatDisplayValue(risk.value);

						return (
							<div key={risk.value} className="flex flex-col p-4 rounded-lg" style={cardStyles}>
								<div className="flex items-center gap-2 mb-2">
									<RiskIcon size={20} color={cardStyles.color} />
									<Typography weight="semibold" size="md" color={cardStyles.color}>
										{displayValue}
									</Typography>
								</div>
								<Typography size="sm" color={cardStyles.color}>
									{t(toTranslationKey(risk.explanationKey))}
								</Typography>
							</div>
						);
					})}
				</div>
			</div>
		</Modal>
	);
};

const AntiFraudActionButtons: React.FC<{
	registry: Registry;
	normalizedData?: NormalizedAntifraudData | null;
	setOpenFraudAnalysisAlertsModal: (value: boolean) => void;
}> = ({ registry, normalizedData: propNormalizedData, setOpenFraudAnalysisAlertsModal }) => {
	const t = useTranslations('RegistryDetails');
	const hookNormalizedData = useNormalizedAntifraudData(registry);
	const normalizedData = propNormalizedData ?? hookNormalizedData;
	const [openRiskValuesModal, setOpenRiskValuesModal] = useState(false);

	const getRiskScoreCardColor = (riskScore: number | null) => {
		if (!riskScore) return { backgroundColor: 'rgba(128, 128, 128, 0.3)', color: 'gray' };
		if (riskScore < 20) {
			return { backgroundColor: 'rgba(16, 130, 82, 0.3)', color: '#108252' };
		}
		if (riskScore < 60 && riskScore >= 20) {
			return { backgroundColor: 'rgba(255, 167, 26, 0.3)', color: '#FFA71A' };
		}
		if (riskScore >= 60) {
			return { backgroundColor: 'rgba(255, 37, 26, 0.3)', color: '#FF251A' };
		}
	};

	const getRiskScoreIcon = (riskScore: number | null) => {
		if (!riskScore) return Clock;
		if (riskScore < 20) {
			return CheckCircle;
		}
		if (riskScore < 60 && riskScore >= 20) {
			return AlertCircle;
		}
		if (riskScore >= 60) {
			return AlertTriangle;
		}
		return Clock;
	};

	const isUnico = normalizedData?.isUnico || false;
	const unicoResult = normalizedData?.unicoResult;
	const trullyScore = normalizedData?.identityFraudScore;
	const previousTrullyScore = !isUnico ? null : trullyScore;

	const cardStyles =
		isUnico && unicoResult
			? getUnicoResultColor(unicoResult)
			: getRiskScoreCardColor(trullyScore || null);

	const RiskIcon =
		isUnico && unicoResult
			? getUnicoResultIcon(unicoResult)
			: getRiskScoreIcon(trullyScore || null);

	const normalizedUnicoResult = unicoResult ? normalizeUnicoResultForDisplay(unicoResult) : null;
	const displayValue =
		isUnico && normalizedUnicoResult
			? normalizedUnicoResult.toUpperCase() === 'EN VIVO'
				? t('ProofOfLife')
				: normalizedUnicoResult.toUpperCase() === 'NO APROBADO'
					? t('DoNotApprove')
					: normalizedUnicoResult.toUpperCase() === 'NO EN VIVO'
						? t('ReviewProofOfLife')
						: formatUnicoStatus(normalizedUnicoResult)
			: trullyScore || t('Pending');

	const explanationKey = isUnico && unicoResult ? getUnicoResultExplanation(unicoResult) : null;

	const warningsCount = normalizedData?.warnings?.filter(Boolean).length || 0;

	return (
		<div className="flex flex-col gap-4 w-1/4">
			<div className="flex flex-col p-4 rounded-lg text-center" style={cardStyles}>
				<div className="flex justify-center -mt-2 mb-2">
					<RiskIcon size={24} color={cardStyles?.color} />
				</div>
				<Typography weight="semibold" type="display" color={cardStyles?.color}>
					{displayValue}
				</Typography>
				{explanationKey && (
					<div className="mt-2">
						<Typography size="sm" color={cardStyles?.color}>
							{t(toTranslationKey(explanationKey))}
						</Typography>
					</div>
				)}
				{previousTrullyScore !== null && (
					<div className="mt-1">
						<Typography size="xs" color={GRAY_COLORS.gray600}>
							{t('PreviousTrullyScore')}: {previousTrullyScore}
						</Typography>
					</div>
				)}
			</div>
			{registry.legalRepresentative?.antifraudAnalysis?.type === UNICO_ANTIFRAUD_TYPE &&
				registry.legalRepresentative?.antifraudAnalysis?.v2Analysis && (
					<div className="flex justify-end -mt-2">
						<Button
							textOnly
							label={t('MoreInformation')}
							onClick={() => setOpenRiskValuesModal(true)}
							color="gray"
						/>
					</div>
				)}
			<div className="flex flex-row gap-1 items-center justify-between">
				<Typography>{t('Alerts')}: </Typography>
				<div className="flex flex-row gap-2">
					<Typography weight="semibold">{warningsCount}</Typography>
					{warningsCount > 0 && (
						<Button
							textOnly
							label={t('Detail')}
							onClick={() => {
								setOpenFraudAnalysisAlertsModal(true);
							}}
							color="gray"
							disabled={warningsCount === 0}
						/>
					)}
				</div>
			</div>
			<UnicoRiskValuesModal
				isOpen={openRiskValuesModal}
				onClose={() => setOpenRiskValuesModal(false)}
			/>
		</div>
	);
};

const AntifraudAnalysisAlertsModal: React.FC<{
	registry: Registry;
	openFraudAnalysisAlertsModal: boolean;
	setOpenFraudAnalysisAlertsModal: (value: boolean) => void;
}> = ({ registry, openFraudAnalysisAlertsModal, setOpenFraudAnalysisAlertsModal }) => {
	const t = useTranslations('RegistryDetails');
	const normalizedData = useNormalizedAntifraudData(registry);
	const warnings = normalizedData?.warnings?.filter(Boolean) || [];

	return (
		<Modal
			isOpen={warnings.length > 0 && openFraudAnalysisAlertsModal}
			onClose={() => {
				setOpenFraudAnalysisAlertsModal(false);
			}}
			maxWidth={600}
			closeable={true}
		>
			<div className="flex flex-col gap-4">
				<div className="flex flex-row gap-2 items-center">
					<AlertTriangle />
					<Typography size="xl">{t('Alerts')}</Typography>
				</div>
				<hr className="flex mb-2" />
				{warnings.map(
					(warning, index) =>
						warning && (
							<Typography key={index}>
								{t('Alert')} #{index + 1}: {warning}
							</Typography>
						)
				)}
			</div>
		</Modal>
	);
};

const ActionButtons: React.FC<{
	registry: Registry;
	onApprove: () => void;
	onReject: () => void;
}> = ({ registry, onApprove, onReject }) => {
	const t = useTranslations('RegistryDetails');

	const validStatusesForApprove = [RegistryStatus.COMPLETED, RegistryStatus.REJECTED];

	return (
		<div className="flex flex-col gap-4 w-1/4">
			<div className="flex gap-2 justify-center space-evenly">
				<Tooltip
					content={
						!registry.registryStatus ||
						!validStatusesForApprove.includes(registry.registryStatus as RegistryStatus)
							? t('ApproveDisabled')
							: ''
					}
				>
					<Button
						size="large"
						label={t('Approve')}
						onClick={onApprove}
						disabled={!validStatusesForApprove.includes(registry.registryStatus as RegistryStatus)}
					/>
				</Tooltip>
				<Tooltip
					content={registry.registryStatus !== RegistryStatus.COMPLETED ? t('RejectDisabled') : ''}
				>
					<Button
						size="large"
						label={t('Reject')}
						color="white"
						outlined
						onClick={onReject}
						disabled={registry.registryStatus !== RegistryStatus.COMPLETED}
					/>
				</Tooltip>
			</div>
		</div>
	);
};

const RegistryDetails: React.FC = () => {
	const { clientId } = useParams<{ clientId: string | '' }>();
	const t = useTranslations('RegistryDetails');
	const onboardingClient = useApolloClient(SCHEMAS.ONBOARDING_LEGAL);

	const {
		loading,
		error,
		data,
		refetch: refetchRegistry
	} = useGetNodeQuery({
		client: onboardingClient,
		variables: { id: clientId || '' }
	});

	const [
		downloadRegistryDocuments,
		{ loading: loadingDownloadRegistryDocuments, data: registryDocumentsData }
	] = useDownloadRegistryDocumentsMutation({
		client: onboardingClient,
		onCompleted: (data: DownloadRegistryDocumentsMutation) => {
			const registryDocuments = data.downloadRegistryDocuments;
			logger.success('Registry documents downloaded', registryDocuments);
			if (registryDocuments?.mutationErrors && registryDocuments.mutationErrors.length > 0) {
				registryDocuments.mutationErrors.forEach((error) => {
					if (error) {
						Sentry.captureException(error);
						switch (error.__typename) {
							case 'InvalidInput':
								logger.error('Invalid input', error.field);
								break;
							default:
								logger.error('Unhandled mutation error', error);
								break;
						}
					}
				});
				return;
			}
		},
		onError: (error) => {
			logger.error('Error downloading registry documents', error);
		}
	});

	useEffect(() => {
		const callDownloadRegistryDocuments = async () => {
			if (data?.node?.clientId) {
				const input: DownloadRegistryDocumentsInput = {
					clientId: data?.node?.clientId
				};
				await downloadRegistryDocuments({ variables: { input } });
			}
		};
		callDownloadRegistryDocuments();
	}, [data, downloadRegistryDocuments]);

	const appRegistrySectionTitles = [
		'ContactInformation',
		'CompanyInformation',
		'LegalRepresentative'
	];

	const trullyAntifraudSectionTitles = [
		'GeolocationAddress',
		'OfficialId',
		'IpAddress',
		'BlackListsPEP',
		'RfcAnalysis',
		'CurpAnalysis'
	];

	const unicoAntifraudSectionTitles = [
		'GeolocationAddress',
		'OfficialId',
		'RfcAnalysis',
		'CurpAnalysis'
	];

	const [openAppRegistrySections, setOpenAppRegistrySections] = useState(
		new Set(appRegistrySectionTitles)
	);
	const [openTrullyAntifraudSections, setOpenTrullyAntifraudSections] = useState(
		new Set(trullyAntifraudSectionTitles)
	);
	const [openUnicoAntifraudSections, setOpenUnicoAntifraudSections] = useState(
		new Set(unicoAntifraudSectionTitles)
	);
	const [openDetailedAnswerModal, setOpenDetailedAnswerModal] = useState(false);
	const [openFraudAnalysisAlertsModal, setOpenFraudAnalysisAlertsModal] = useState(true);
	const [detailedAnswerModalContent, setDetailedAnswerModalContent] = useState([]);
	const [openRejectModal, setOpenRejectModal] = useState(false);
	const [openApproveModal, setOpenApproveModal] = useState(false);
	const [isDeliberationOpen, setIsDeliberationOpen] = useState(false);
	const [drawerWidth, setDrawerWidth] = useState(300);
	const [isResizing, setIsResizing] = useState(false);

	const registry = data?.node;
	const separatedAntifraudData = useSeparatedAntifraudData(
		registry && registry.__typename === 'Registry' ? registry : null
	);

	const toggleSection = (
		title: string,
		setOpenSections: React.Dispatch<React.SetStateAction<Set<string>>>
	) => {
		setOpenSections((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(title)) {
				newSet.delete(title);
			} else {
				newSet.add(title);
			}
			return newSet;
		});
	};

	if (loading || loadingDownloadRegistryDocuments) return <LoadingSpinner />;
	if (error) return <ErrorMessage message={t('ErrorLoadingRegistry')} />;

	if (!registry || registry.__typename !== 'Registry') {
		return <ErrorMessage message={t('RegistryNotFound')} />;
	}

	return (
		<>
			<Header registry={registry} openDeliverationDrawer={() => setIsDeliberationOpen(true)} />
			<div className="flex flex-col gap-4 p-6">
				<div className="mt-32"></div>
				<StatusTimeline registry={registry} />
				<DocumentsSection
					documents={
						registryDocumentsData?.downloadRegistryDocuments?.result ?? ({} as RegistryDocuments)
					}
				/>
				<SectionControls
					title={t('AppRegistry')}
					openSections={openAppRegistrySections}
					sectionTitles={appRegistrySectionTitles}
					onExpandAll={() => setOpenAppRegistrySections(new Set(appRegistrySectionTitles))}
					onCollapseAll={() => setOpenAppRegistrySections(new Set())}
				/>

				<div className="flex flex-row gap-5 mb-5 bg-slate-50 p-4 rounded-lg">
					<div className="flex flex-col gap-2 w-3/4">
						{appRegistrySectionTitles.map((title) => (
							<RegistrySection
								key={title}
								title={title}
								isOpen={openAppRegistrySections.has(title)}
								onToggle={() => toggleSection(title, setOpenAppRegistrySections)}
							>
								{(() => {
									switch (title) {
										case 'ContactInformation':
											return <ContactInformation registry={registry} />;
										case 'CompanyInformation':
											return <CompanyInformation registry={registry} />;
										case 'LegalRepresentative':
											return <LegalRepresentative registry={registry} />;
									}
								})()}
							</RegistrySection>
						))}
					</div>
					<ActionButtons
						registry={registry}
						onApprove={() => setOpenApproveModal(true)}
						onReject={() => setOpenRejectModal(true)}
					/>
				</div>

				{separatedAntifraudData.trully && (
					<>
						<SectionControls
							title={t('TrullyAntifraud')}
							openSections={openTrullyAntifraudSections}
							sectionTitles={trullyAntifraudSectionTitles}
							onExpandAll={() =>
								setOpenTrullyAntifraudSections(new Set(trullyAntifraudSectionTitles))
							}
							onCollapseAll={() => setOpenTrullyAntifraudSections(new Set())}
						/>
						<div className="flex flex-row gap-5 mb-5 bg-slate-50 p-4 rounded-lg">
							<div className="flex flex-col gap-2 w-3/4">
								{trullyAntifraudSectionTitles.map((title) => (
									<RegistrySection
										key={`trully-${title}`}
										title={title}
										isOpen={openTrullyAntifraudSections.has(title)}
										onToggle={() => toggleSection(title, setOpenTrullyAntifraudSections)}
									>
										{(() => {
											switch (title) {
												case 'GeolocationAddress':
													return (
														<GeolocationAddress
															registry={registry}
															normalizedData={separatedAntifraudData.trully}
														/>
													);
												case 'OfficialId':
													return (
														<OfficialId
															registry={registry}
															normalizedData={separatedAntifraudData.trully}
															setOpenDetailedAnswerModal={setOpenDetailedAnswerModal}
															setDetailedAnswerModalContent={setDetailedAnswerModalContent}
														/>
													);
												case 'IpAddress':
													return (
														<IpAddress
															registry={registry}
															normalizedData={separatedAntifraudData.trully}
														/>
													);
												case 'BlackListsPEP':
													return (
														<BlackListsPEP
															registry={registry}
															normalizedData={separatedAntifraudData.trully}
															setOpenDetailedAnswerModal={setOpenDetailedAnswerModal}
															setDetailedAnswerModalContent={setDetailedAnswerModalContent}
														/>
													);
												case 'RfcAnalysis':
													return <Rfc registry={registry} />;
												case 'CurpAnalysis':
													return <Curp registry={registry} />;
												default:
													return null;
											}
										})()}
									</RegistrySection>
								))}
							</div>
							<AntiFraudActionButtons
								registry={registry}
								normalizedData={separatedAntifraudData.trully}
								setOpenFraudAnalysisAlertsModal={setOpenFraudAnalysisAlertsModal}
							/>
						</div>
					</>
				)}

				{separatedAntifraudData.unico && (
					<>
						<SectionControls
							title={t('UnicoAntifraud')}
							openSections={openUnicoAntifraudSections}
							sectionTitles={unicoAntifraudSectionTitles}
							onExpandAll={() =>
								setOpenUnicoAntifraudSections(new Set(unicoAntifraudSectionTitles))
							}
							onCollapseAll={() => setOpenUnicoAntifraudSections(new Set())}
						/>
						<div className="flex flex-row gap-5 mb-5 bg-slate-50 p-4 rounded-lg">
							<div className="flex flex-col gap-2 w-3/4">
								{unicoAntifraudSectionTitles.map((title) => (
									<RegistrySection
										key={`unico-${title}`}
										title={title}
										isOpen={openUnicoAntifraudSections.has(title)}
										onToggle={() => toggleSection(title, setOpenUnicoAntifraudSections)}
									>
										{(() => {
											switch (title) {
												case 'GeolocationAddress':
													return (
														<GeolocationAddress
															registry={registry}
															normalizedData={separatedAntifraudData.unico}
														/>
													);
												case 'OfficialId':
													return (
														<OfficialId
															registry={registry}
															normalizedData={separatedAntifraudData.unico}
															setOpenDetailedAnswerModal={setOpenDetailedAnswerModal}
															setDetailedAnswerModalContent={setDetailedAnswerModalContent}
														/>
													);
												case 'RfcAnalysis':
													return <Rfc registry={registry} />;
												case 'CurpAnalysis':
													return <Curp registry={registry} />;
												default:
													return null;
											}
										})()}
									</RegistrySection>
								))}
							</div>
							<AntiFraudActionButtons
								registry={registry}
								normalizedData={separatedAntifraudData.unico}
								setOpenFraudAnalysisAlertsModal={setOpenFraudAnalysisAlertsModal}
							/>
						</div>
					</>
				)}

				{data?.node?.clientId && (
					<RejectRegistryModal
						isOpen={openRejectModal}
						onClose={() => setOpenRejectModal(false)}
						onSuccess={async () => {
							await refetchRegistry();
							toast.success(t('RegistryRejectedSuccessfully'));
						}}
						clientId={data.node.clientId}
					/>
				)}
				{data?.node?.clientId && (
					<ApproveRegistryModal
						isOpen={openApproveModal}
						onClose={() => setOpenApproveModal(false)}
						onSuccess={async () => {
							await refetchRegistry();
							toast.success(t('RegistryApprovedSuccessfully'));
						}}
						clientId={data.node.clientId}
					/>
				)}
				<AntifraudAnalysisAlertsModal
					registry={registry}
					openFraudAnalysisAlertsModal={openFraudAnalysisAlertsModal}
					setOpenFraudAnalysisAlertsModal={setOpenFraudAnalysisAlertsModal}
				/>
				<Modal
					isOpen={openDetailedAnswerModal}
					onClose={() => {
						setOpenDetailedAnswerModal(false);
					}}
					maxWidth={1000}
					closeable={true}
				>
					<div className="flex flex-col gap-4">
						<Typography size="xl">{t('MoreInformation')}</Typography>
						{detailedAnswerModalContent.map((json) => (
							<JsonView data={json} shouldExpandNode={allExpanded} style={defaultStyles} />
						))}
					</div>
				</Modal>
				<DeliberationDrawer
					isOpen={isDeliberationOpen}
					onClose={() => setIsDeliberationOpen(false)}
					deliberation={registry?.deliberation || ''}
					t={t}
					drawerWidth={drawerWidth}
					onDrawerWidthChange={setDrawerWidth}
					isResizing={isResizing}
					onResizingChange={setIsResizing}
				/>
			</div>
		</>
	);
};

export default RegistryDetails;
